import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Heart, Users, Trophy } from 'lucide-react';
import './About.css';

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const specialPrograms = [
  { name: 'Adaptive Fitness', description: 'Inclusive programs designed for children with special needs, focusing on physical activity and fun.' },
  { name: 'Sensory Play', description: 'Activities that engage the senses and promote motor skills development.' },
  { name: 'Social Skills Development', description: 'Group activities that foster communication and teamwork among peers.' },
  { name: 'Therapeutic Martial Arts', description: 'Martial arts classes tailored to enhance focus, discipline, and self-defense skills.' },
  { name: 'Family Fitness Days', description: 'Events that encourage family participation and support for children with special needs.' }
];

const values = [
  { icon: Star, name: 'Inclusivity', description: 'Creating a welcoming environment for all children.' },
  { icon: Heart, name: 'Empathy', description: 'Understanding and supporting each child’s unique journey.' },
  { icon: Users, name: 'Community', description: 'Building a supportive network for families and children.' },
  { icon: Trophy, name: 'Growth', description: 'Encouraging personal development and achievement.' }
];

export default function SpecialKids() {
  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-green-50">
        <main className="container mx-auto px-4 py-8">
          <section className="mb-16" style={{
             backgroundImage: 'url(/img.jpg) ', 
             backgroundSize: 'cover', 
             backgroundPosition: 'center', 
             borderRadius: '1rem', 
             paddingBottom: '6rem', paddingLeft: '6rem', paddingRight: '6rem',
             backgroundBlendMode: 'multiply', 
             backgroundColor: 'rgba(0, 0, 0, 0.5)', 
             paddingTop: '6rem',color: 'white' 
             }}>
            <motion.h2 
              className="text-4xl font-bold mb-4 "
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Empowering Special Kids Through Fitness
            </motion.h2>
            <motion.p 
              className="text-xl  mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Providing a safe, nurturing, and fun environment for children with special needs to thrive.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Link 
                to="/programs"
                className="bg-blue-600 text-white px-6 py-3 rounded-full inline-flex items-center hover:bg-blue-700 transition-colors duration-300 text-decoration-none"
              >
                Explore Our Special Programs <ArrowRight className="ml-2" />
              </Link>
            </motion.div>
          </section>

          <section className="mb-16">
            <motion.h3 
              className="text-3xl font-bold mb-6 text-gray-800"
              {...fadeIn}
            >
              Our Commitment
            </motion.h3>
            <motion.p 
              className="text-lg text-gray-600 mb-4"
              {...fadeIn}
            >
              At MY KIDS Fitness Solutions, we are dedicated to providing specialized programs that cater to the unique needs of children with special needs. Our experienced instructors are trained to create an inclusive and supportive environment where every child can flourish.
            </motion.p>
            <motion.p 
              className="text-lg text-gray-600"
              {...fadeIn}
            >
              We believe that every child deserves the opportunity to engage in physical activity, build confidence, and develop social skills. Our programs are designed to be fun, engaging, and tailored to each child's abilities.
            </motion.p>
          </section>

          <section className="mb-16">
            <motion.h3 
              className="text-3xl font-bold mb-6 text-gray-800"
              {...fadeIn}
            >
              Our Special Programs
            </motion.h3>
            <div className="grid md:grid-cols-2 gap-6">
              {specialPrograms.map((program, index) => (
                <motion.div 
                  key={index}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <h4 className="text-xl font-semibold mb-2 text-blue-600">{program.name}</h4>
                  <p className="text-gray-600">{program.description}</p>
                </motion.div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <motion.h3 
              className="text-3xl font-bold mb-6 text-gray-800"
              {...fadeIn}
            >
              Our Core Values
            </motion.h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <motion.div 
                  key={index}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 text-center"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <value.icon className="w-12 h-12 mx-auto mb-4 text-blue-600" />
                  <h4 className="text-xl font-semibold mb-2 text-gray-800">{value.name}</h4>
                  <p className="text-gray-600">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </section>

          <section className="text-center mb-16">
            <motion.h3 
              className="text-3xl font-bold mb-6 text-gray-800"
              {...fadeIn}
            >
              Join Our Special Kids Family
            </motion.h3>
            <motion.p 
              className="text-lg text-gray-600 mb-8"
              {...fadeIn}
            >
              Experience the joy of inclusive fitness and watch your child thrive in a supportive, fun-filled environment. Together, we can make a difference!
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Link 
                to="/contact"
                className="bg-green-500 text-white px-6 py-3 rounded-full inline-flex items-center hover:bg-green-600 transition-colors duration-300 text-decoration-none"
              >
                Contact Us Today <ArrowRight className="ml-2" />
              </Link>
            </motion.div>
          </section>
        </main>
      </div>
    </>
  );
}
